import type { DynamicIslandActivity } from '../../../../context/dynamicIslandTypes'
import { HorizontalChipStrip } from '../../../HorizontalChipStrip'
import {
  activityIslandAccent,
  activityPreviewProgress,
  sortActivitiesByCategory,
} from '../../../../lib/dynamicIsland/categoryOrder'
import {
  dynamicIslandAccentClass,
  dynamicIslandProgressFillClass,
  dynamicIslandProgressTrackClass,
  dynamicIslandStatusChipClass,
} from '../../../../lib/dynamicIsland/dynamicIslandPresentation'

type NavProps = {
  activities: DynamicIslandActivity[]
  selectedId: string
  onSelectId: (id: string) => void
  angular: boolean
}

function moduleProgress(activity: DynamicIslandActivity, angular: boolean) {
  const progress = activityPreviewProgress(activity)
  const accent = activityIslandAccent(activity)
  const show = progress > 0 || activity.lifecycle === 'active'
  if (!show) return null

  const track = dynamicIslandProgressTrackClass(angular)
  const fill = dynamicIslandProgressFillClass({
    accent,
    progressPercent: progress,
    lifecycle: activity.lifecycle,
  })

  return (
    <span className={['glyphicai-dynamic-island-detail-module__progress', track].join(' ')}>
      <span
        className={['glyphicai-dynamic-island-activity__progress-fill', fill].join(' ')}
        style={{ width: `${progress}%` }}
      />
    </span>
  )
}

function ModuleRailItem({
  activity,
  selected,
  angular,
  onClick,
}: {
  activity: DynamicIslandActivity
  selected: boolean
  angular: boolean
  onClick: () => void
}) {
  const accent = activityIslandAccent(activity)
  const { expanded, sourceLabel, lifecycle } = activity

  return (
    <button
      type="button"
      className={[
        'glyphicai-dynamic-island-detail-module',
        'glyphicai-dynamic-island-detail-module--rail',
        selected ? 'glyphicai-dynamic-island-detail-module--selected' : '',
        angular ? 'glyphicai-dynamic-island-detail-module--angular' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-pressed={selected}
      onClick={onClick}
    >
      <span
        className={[
          'glyphicai-dynamic-island-activity__accent',
          dynamicIslandAccentClass(accent),
        ].join(' ')}
        aria-hidden
      />
      <span className="glyphicai-dynamic-island-detail-module__body">
        <span className="glyphicai-dynamic-island-detail-module__meta">
          <span className="glyphicai-dynamic-island-detail-module__source">{sourceLabel}</span>
          {lifecycle === 'active' && expanded.statusLabel ? (
            <span className={dynamicIslandStatusChipClass(accent)}>{expanded.statusLabel}</span>
          ) : null}
        </span>
        <span className="glyphicai-dynamic-island-detail-module__title">{expanded.title}</span>
        <span className="glyphicai-dynamic-island-detail-module__desc">{expanded.description}</span>
        {moduleProgress(activity, angular)}
      </span>
    </button>
  )
}

function ModuleChipItem({
  activity,
  selected,
  onClick,
}: {
  activity: DynamicIslandActivity
  selected: boolean
  onClick: () => void
}) {
  const accent = activityIslandAccent(activity)

  return (
    <button
      type="button"
      className={[
        'glyphicai-dynamic-island-detail-module',
        'glyphicai-dynamic-island-detail-module--chip',
        selected ? 'glyphicai-dynamic-island-detail-module--selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-pressed={selected}
      onClick={onClick}
    >
      <span
        className={[
          'glyphicai-dynamic-island-activity__accent',
          'glyphicai-dynamic-island-detail-module__chip-accent',
          dynamicIslandAccentClass(accent),
        ].join(' ')}
        aria-hidden
      />
      <span className="glyphicai-dynamic-island-detail-module__chip-label">{activity.sourceLabel}</span>
    </button>
  )
}

export function DynamicIslandActivityModuleRail({
  activities,
  selectedId,
  onSelectId,
  angular,
}: NavProps) {
  const ordered = sortActivitiesByCategory(activities)
  if (ordered.length <= 1) return null

  return (
    <nav className="glyphicai-dynamic-island-detail-rail" aria-label="后台模块">
      <div className="glyphicai-dynamic-island-detail-rail__list">
        {ordered.map((activity) => (
          <ModuleRailItem
            key={activity.id}
            activity={activity}
            selected={activity.id === selectedId}
            angular={angular}
            onClick={() => onSelectId(activity.id)}
          />
        ))}
      </div>
    </nav>
  )
}

export function DynamicIslandActivityModuleChipStrip({
  activities,
  selectedId,
  onSelectId,
}: NavProps) {
  const ordered = sortActivitiesByCategory(activities)
  if (ordered.length <= 1) return null

  return (
    <HorizontalChipStrip
      className="glyphicai-dynamic-island-detail-chipstrip"
      trackClassName="gap-1.5 px-0.5"
      aria-label="后台模块"
    >
      {ordered.map((activity) => (
        <ModuleChipItem
          key={activity.id}
          activity={activity}
          selected={activity.id === selectedId}
          onClick={() => onSelectId(activity.id)}
        />
      ))}
    </HorizontalChipStrip>
  )
}
